import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

// The mobile app's snack bars: a short line at the bottom, with at most one action.

interface Toast {
  id: number;
  text: string;
  action?: { label: string; run: () => void };
}

type Show = (text: string, action?: Toast['action']) => void;

const ToastContext = createContext<Show>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const next = useRef(1);

  const dismiss = useCallback((id: number) => setToasts((all) => all.filter((t) => t.id !== id)), []);

  const show = useCallback<Show>(
    (text, action) => {
      const id = next.current++;
      // A new message replaces the old one, as snack bars do.
      setToasts([{ id, text, action }]);
      setTimeout(() => dismiss(id), action ? 7000 : 4000);
    },
    [dismiss],
  );

  const value = useMemo(() => show, [show]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className="toast">
            <span>{t.text}</span>
            {t.action && (
              <button
                type="button"
                className="btn btn-text"
                onClick={() => {
                  dismiss(t.id);
                  t.action!.run();
                }}
              >
                {t.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
